/**
 * Versions and manual edits of artifacts: typed client over the local `api`
 * helper (plan 5.2, WP1 and WP5).
 *
 * Every response is the `{ data, error, meta }` envelope; failures throw an
 * `ArtifactsApiError` with the code and `meta.details` (for example
 * `headVersionId` on a 409 `ARTIFACT_VERSION_CONFLICT`, or `issues` on a 422
 * `ARTIFACT_EDIT_INVALID`). The request language goes along
 * (`Accept-Language`), so a 422 or 429 message comes back in the app's
 * language and can be shown as it is.
 */
import type {
  ArtifactDocument,
  ArtifactEditVia,
  ArtifactEditorSource,
  ArtifactVersionDetail,
  ArtifactVersionList,
  ArtifactVersionRestoreResult,
  ArtifactVersionSummary,
  PageEditOp,
  PageEditsResult,
} from '@tabbio/types';
import i18n from '@/i18n';
import { api } from './api';
import { unwrapArtifactsResponse as unwrap } from './artifacts-api';
import { resolveDeviceTimezone } from './device-timezone';

const path = (id: string) => `/api/artifacts/${encodeURIComponent(id)}`;

function headers(): Record<string, string> {
  const timezone = resolveDeviceTimezone();
  return {
    'Accept-Language': i18n.language || 'en',
    ...(timezone ? { 'X-Timezone': timezone } : {}),
  };
}

/** Marks a save that applies a suggestion (the API's analytics read it). */
export type ManualEditVia = ArtifactEditVia;

/**
 * Answer of `PUT /api/artifacts/:id/document` (`ArtifactDocumentSaveResult`
 * in `packages/types`, which the package index does not export yet).
 */
export type ArtifactDocumentSaveResult = {
  version: ArtifactVersionSummary;
  squashed: boolean;
};

// ── Versions ────────────────────────────────────────────────────────────────

/** GET /api/artifacts/:id/versions?limit=&cursor= (newest first). */
export async function listArtifactVersions(
  id: string,
  params: { limit?: number; cursor?: string | null } = {},
): Promise<ArtifactVersionList> {
  const query = new URLSearchParams();
  query.set('limit', String(params.limit ?? 50));
  if (params.cursor) query.set('cursor', params.cursor);
  return unwrap(
    await api.get<ArtifactVersionList>(`${path(id)}/versions?${query}`, { headers: headers() }),
  );
}

/** GET /api/artifacts/:id/versions/:versionId */
export async function getArtifactVersion(
  id: string,
  versionId: string,
): Promise<ArtifactVersionDetail> {
  return unwrap(
    await api.get<ArtifactVersionDetail>(
      `${path(id)}/versions/${encodeURIComponent(versionId)}`,
      { headers: headers() },
    ),
  );
}

/** POST /api/artifacts/:id/versions/:versionId/restore (a new head version). */
export async function restoreArtifactVersion(
  id: string,
  versionId: string,
): Promise<ArtifactVersionRestoreResult> {
  return unwrap(
    await api.post<ArtifactVersionRestoreResult>(
      `${path(id)}/versions/${encodeURIComponent(versionId)}/restore`,
      {},
      { headers: headers() },
    ),
  );
}

// ── Pages ───────────────────────────────────────────────────────────────────

/** GET /api/artifacts/:id/editor-source?versionId= (the numbered page and the bridge hash). */
export async function getEditorSource(
  id: string,
  versionId?: string | null,
): Promise<ArtifactEditorSource> {
  const query = versionId ? `?versionId=${encodeURIComponent(versionId)}` : '';
  return unwrap(
    await api.get<ArtifactEditorSource>(`${path(id)}/editor-source${query}`, { headers: headers() }),
  );
}

/** POST /api/artifacts/:id/page-edits */
export async function savePageEdits(
  id: string,
  body: { baseVersionId: string; editSessionId: string; ops: PageEditOp[]; via?: ManualEditVia },
): Promise<PageEditsResult> {
  return unwrap(
    await api.post<PageEditsResult>(`${path(id)}/page-edits`, body, { headers: headers() }),
  );
}

/** PUT /api/artifacts/:id/page-source (the web Code tab). */
export async function savePageSource(
  id: string,
  body: { baseVersionId: string; editSessionId: string; html: string },
): Promise<PageEditsResult> {
  return unwrap(
    await api.put<PageEditsResult>(`${path(id)}/page-source`, body, { headers: headers() }),
  );
}

// ── Documents and slides ────────────────────────────────────────────────────

/** PUT /api/artifacts/:id/document (the whole document, schemaVersion 2). */
export async function saveArtifactDocument(
  id: string,
  body: {
    baseVersionId: string;
    editSessionId: string;
    document: ArtifactDocument;
    via?: ManualEditVia;
  },
): Promise<ArtifactDocumentSaveResult> {
  return unwrap(
    await api.put<ArtifactDocumentSaveResult>(`${path(id)}/document`, body, { headers: headers() }),
  );
}

// ── Images ──────────────────────────────────────────────────────────────────

/** POST /api/artifacts/:id/image-edits (202: the new version is pending). */
export async function requestArtifactImageEdit(
  id: string,
  body: { baseVersionId?: string; instruction: string },
): Promise<{ version: ArtifactVersionSummary }> {
  return unwrap(
    await api.post<{ version: ArtifactVersionSummary }>(`${path(id)}/image-edits`, body, {
      headers: headers(),
    }),
  );
}
